import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatError } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Stethoscope, KeyRound } from "lucide-react";
import { toast } from "sonner";
import TermsCheckbox from "@/components/TermsCheckbox";

export default function DoctorLogin() {
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [step, setStep] = useState("identify"); // identify | setup | password
  const [mobile, setMobile] = useState("");
  const [cid, setCid] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [password, setPassword] = useState("");
  const [mpin, setMpin] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [mode, setMode] = useState("password"); // password | mpin
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const checkMobile = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/doctor/first-login", { mobile });
      setCid(data.cid);
      setDoctorName(data.name);
      if (data.needs_setup) {
        toast.success(`Welcome ${data.name}! Your CID is ${data.cid}. Please set your credentials.`);
        setStep("setup");
      } else {
        setStep("password");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const setupCreds = async (e) => {
    e.preventDefault();
    if (password !== confirmPwd) {
      toast.error("Passwords do not match");
      return;
    }
    if (!/^\d{4,6}$/.test(mpin)) {
      toast.error("MPIN must be 4-6 digits");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/doctor/set-credentials", { mobile, password, mpin, accepted_terms: true });
      setSession(data);
      toast.success("Credentials set. Welcome!");
      navigate("/doctor/profile-setup");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const credential = mode === "password" ? password : mpin;
      const { data } = await api.post("/auth/doctor/login", {
        identifier: mobile,
        credential,
        mode,
      });
      setSession(data);
      toast.success("Welcome back");
      navigate(data.user?.profile_complete ? "/doctor/dashboard" : "/doctor/profile-setup");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 bg-grain flex flex-col" data-testid="doctor-login-page">
      <header className="px-6 md:px-12 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-gateway">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 pb-16">
        <Card className="w-full max-w-md p-8 medical-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center">
              <Stethoscope className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">Doctor Portal</h1>
              <p className="text-xs text-slate-500">Login with mobile, CID or MPIN</p>
            </div>
          </div>

          {step === "identify" && (
            <form onSubmit={checkMobile} className="space-y-4">
              <div>
                <Label htmlFor="mobile">Registered Mobile Number</Label>
                <Input
                  id="mobile" value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  required minLength={10} maxLength={10}
                  data-testid="doctor-mobile-input"
                  placeholder="10-digit mobile"
                  inputMode="numeric"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Use the mobile your Hospital/Clinic registered for you.
                </p>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="doctor-continue-btn">
                {submitting ? "Checking..." : "Continue"}
              </Button>
            </form>
          )}

          {step === "setup" && (
            <form onSubmit={setupCreds} className="space-y-4">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Your Permanent CID</p>
                <p className="font-heading text-2xl font-bold text-emerald-700 mt-1 tracking-widest" data-testid="doctor-cid-display">{cid}</p>
                <p className="text-xs text-slate-600 mt-2">Welcome {doctorName}. Save this 8-digit ID for fast login.</p>
              </div>

              <div>
                <Label htmlFor="setup-pwd">Set Password</Label>
                <Input id="setup-pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="doctor-setup-password" />
              </div>
              <div>
                <Label htmlFor="setup-confirm">Confirm Password</Label>
                <Input id="setup-confirm" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required minLength={6} data-testid="doctor-setup-confirm" />
              </div>
              <div>
                <Label htmlFor="setup-mpin">Set MPIN (4-6 digits, for fast login)</Label>
                <Input id="setup-mpin" value={mpin} onChange={(e) => setMpin(e.target.value.replace(/\D/g, "").slice(0, 6))} required minLength={4} maxLength={6} inputMode="numeric" data-testid="doctor-setup-mpin" />
              </div>

              <TermsCheckbox
                checked={acceptedTerms}
                onChange={setAcceptedTerms}
                testid="doctor-terms-checkbox"
              />

              <Button type="submit" disabled={submitting || !acceptedTerms} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="doctor-setup-submit">
                {submitting ? "Saving..." : "Save & Continue"}
              </Button>
            </form>
          )}

          {step === "password" && (
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="password" data-testid="tab-pwd">Password</TabsTrigger>
                <TabsTrigger value="mpin" data-testid="tab-mpin"><KeyRound className="w-3 h-3 mr-1" />MPIN</TabsTrigger>
              </TabsList>

              <form onSubmit={login} className="mt-6 space-y-4">
                {mode === "password" ? (
                  <div>
                    <Label htmlFor="d-pwd">Password</Label>
                    <Input id="d-pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="doctor-password-input" />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="d-mpin">MPIN</Label>
                    <Input id="d-mpin" value={mpin} onChange={(e) => setMpin(e.target.value.replace(/\D/g, "").slice(0, 6))} required minLength={4} maxLength={6} inputMode="numeric" data-testid="doctor-mpin-input" />
                  </div>
                )}
                <Button type="submit" disabled={submitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="doctor-login-submit">
                  {submitting ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </Tabs>
          )}
        </Card>
      </main>
    </div>
  );
}
